// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import {
  categorizeBenchmark,
  formattedBenchmarksRegistry,
} from '../../utils/benchmark-formatter';
import { screenerApi } from '@shared/data-access/api/ai/screener-api';
import { groupBy } from '@shared/data-access/utils/groupby';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Represents a benchmark option in the selector */
export interface BenchmarkOption {
  /** The unique identifier for the benchmark (typically the ticker) */
  value: string;

  /** The display name or description of the benchmark */
  label: string;

  /** The ticker symbol of the benchmark */
  ticker: string;
}

/** Arguments for the useBenchmarkSelector hook */
export interface UseBenchmarkSelectorArgs {
  /** The currently selected benchmark value. Defaults to 'SPY' if not provided */
  value?: string;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Callback function triggered when a benchmark is selected and confirmed */
  onChange?: (value: string) => void;

  /** Optional list of saved benchmark options to display in a separate group */
  savedBenchmarks?: BenchmarkOption[];
}

/**
 * Custom hook that provides state management and business logic for the
 * BenchmarkSelector component.
 *
 * @example
 *
 * ```tsx
 * const { currentBenchmarkLabel, open, onOpen, onClose, onSave } =
 *   useBenchmarkSelector({
 *     value: 'SPY',
 *     disabled: false,
 *     onChange: newValue => console.log(`Selected: ${newValue}`),
 *   });
 *
 * return (
 *   <div>
 *     <button onClick={onOpen}>Open Selector</button>
 *     {open && <SelectorDialog onClose={onClose} onSave={onSave} />}
 *     <p>Selected Benchmark: {currentBenchmarkLabel}</p>
 *   </div>
 * );
 * ```
 */
export const useBenchmarkSelector = ({
  value = 'SPY',
  disabled,
  onChange,
  savedBenchmarks = [],
}: UseBenchmarkSelectorArgs) => {
  const { t } = useTranslation();

  // Fetch benchmark data from API
  const { data, ...benchmarksQuery } = screenerApi.useGetStockBenchmarksQuery();

  // Dialog open state
  const [open, setOpen] = useState(false);

  // Search query state
  const [searchQuery, setSearchQuery] = useState('');

  // Currently selected benchmark
  const [selectedBenchmark, setSelectedBenchmark] = useState(value);

  /** Benchmark data with fallback to default values if API data is not available */
  const benchmarks = useMemo(
    () =>
      data ?? [
        {
          key: 'SPY',
          description: 'SPDR S&P 500',
          ticker: 'SPY',
        },
        {
          key: 'QQQ',
          description: 'Invesco QQQ Trust Series 1',
          ticker: 'QQQ',
        },
      ],
    [data],
  );

  /** Transform raw benchmark data into BenchmarkOption objects */
  const benchmarkOptions: Array<BenchmarkOption & { group: string }> = useMemo(
    () => [
      ...savedBenchmarks.map(benchmark => ({
        value: benchmark.ticker,
        label: benchmark.label,
        ticker: benchmark.ticker,
        group: t('benchmark-selector.groups.saved', { ns: 'components' }),
      })),
      ...benchmarks.map(benchmark => ({
        value: benchmark.ticker,
        label:
          formattedBenchmarksRegistry.get(benchmark.ticker) ??
          benchmark.description,
        ticker: benchmark.ticker,
        group: categorizeBenchmark(benchmark.ticker, t),
      })),
    ],
    [benchmarks, savedBenchmarks, t],
  );

  /** Group benchmarks into saved benchmarks and all other benchmarks */
  const groupedBenchmarks = useMemo(() => {
    // Group benchmarks by their category
    const groups = Array.from(groupBy(benchmarkOptions, 'group').entries());

    // Define the order of groups and filter out any empty groups
    const sortedGroups = [
      t('benchmark-selector.groups.saved', {
        ns: 'components',
      }),
      t('benchmark-selector.categories.popular-us-indexes', {
        ns: 'components',
      }),
      t('benchmark-selector.categories.popular-international-indexes', {
        ns: 'components',
      }),
      t('benchmark-selector.categories.popular-thematics', {
        ns: 'components',
      }),
      t('benchmark-selector.categories.sectors', {
        ns: 'components',
      }),
      t('benchmark-selector.categories.esg', {
        ns: 'components',
      }),
      t('benchmark-selector.categories.other', {
        ns: 'components',
      }),
    ]
      .map(groupName => groups.find(([key]) => key === groupName))
      .filter(Boolean) as typeof groups;

    return sortedGroups;
  }, [benchmarkOptions, t]);

  /** Filter benchmarks based on search query */
  const filteredBenchmarks = useMemo(() => {
    // If search query is empty, return all benchmarks
    if (!searchQuery.trim()) return groupedBenchmarks;

    const query = searchQuery.toLowerCase();

    // Filter each group's benchmarks and only keep groups with matches
    return groupedBenchmarks
      .map(([groupName, benchmarks]) => {
        // Filter benchmarks within this group
        const filteredGroupBenchmarks = benchmarks.filter(
          benchmark =>
            benchmark.label.toLowerCase().includes(query) ||
            benchmark.ticker.toLowerCase().includes(query),
        );

        // Return the group with filtered benchmarks
        return [groupName, filteredGroupBenchmarks] as [string, BenchmarkOption[]];
      })
      .filter(([_, benchmarks]) => benchmarks.length > 0); // Remove empty groups
  }, [groupedBenchmarks, searchQuery]);

  /** Get the display label for the currently selected benchmark */
  const currentBenchmarkLabel = useMemo(() => {
    if (benchmarksQuery.isLoading)
      return `${t('common.loading', { ns: 'common' })}...`;

    const benchmark = benchmarkOptions.find(b => b.value === selectedBenchmark);

    return benchmark?.label;
  }, [benchmarkOptions, benchmarksQuery.isLoading, t, selectedBenchmark]);

  /** Handler for opening the benchmark selector dialog */
  const onOpen = useCallback(() => {
    if (!disabled) {
      setOpen(true);
      setSearchQuery('');
    }
  }, [disabled]);

  /** Handler for closing the benchmark selector dialog */
  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  /** Handler for saving the selected benchmark and closing the dialog */
  const onSave = useCallback(() => {
    if (onChange) {
      onChange(selectedBenchmark);
    }

    onClose();
  }, [onChange, onClose, selectedBenchmark]);

  /** Handler for selecting a benchmark in the list */
  const onSelectBenchmark = useCallback((value: string) => {
    setSelectedBenchmark(value);
  }, []);

  /** Handler for refreshing the benchmark data */
  const onRefresh = useCallback(() => {
    benchmarksQuery.refetch();
  }, [benchmarksQuery]);

  // Return all state and handlers needed by the component
  return {
    benchmarkOptions,
    benchmarks,
    benchmarksQuery,
    currentBenchmarkLabel,
    filteredBenchmarks,
    groupedBenchmarks,
    isFetching: benchmarksQuery.isFetching,
    open,
    searchQuery,
    selectedBenchmark,
    onClose,
    onOpen,
    onRefresh,
    onSelectBenchmark,
    onSave,
    setSearchQuery,
    setSelectedBenchmark,
  } as const;
};
